import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import AxiosClient from '@/api/client';
import { ArrowLeft, Menu, FileText } from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { API_BASE_URL } from '@/config/constants';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SharedAssessmentData } from '@/lib/shared-data';
import { AssessmentMainContent } from '@/components/assessments/assessment-main-content';
import { AssessmentFooter } from '@/components/assessments/assessment-footer';
import { MediaViewerModal } from '@/components/assessments/media-viewer-modal';
import { ContentProtectionWrapper } from '@/components/protection/content-protection-wrapper';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-breakpoint';
import { PropertyType } from '@/lib/shared-data';
import {
  useAssessmentNavigationQuery,
  useTouchpointStandardsQuery,
  useSubmitAssessmentMutation,
} from '@/hooks/use-assessment-performance';
import type { AssessmentPerformanceStandard, AssessmentPerformanceTitle } from '@/types/api';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  updateCurrentAssessmentAnswer,
  setCurrentAssessment,
  incrementPendingMedia,
  clearAssessmentData,
  type MediaAttachment,
} from '@/store/assessments-slice';

// Helper function to compress images
const compressImage = (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // Calculate new dimensions (max 1920x1920)
        let width = img.width;
        let height = img.height;
        const maxDimension = 1920;

        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height / width) * maxDimension;
            width = maxDimension;
          } else {
            width = (width / height) * maxDimension;
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          },
          'image/jpeg',
          0.8 // Quality (0.8 = 80%)
        );
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

export default function AssessmentPerform() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { id } = useParams<{ id: string }>();
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const { hasRole, user } = useAuth();
  const isManager = hasRole('manager');
  const isReadOnly = isManager; // Managers have read-only access

  
  const currentAssessment = useAppSelector(
    state => state.assessments.currentAssessment
  );

  // Navigation state - MUST be declared before using in hooks
  const [selectedTouchpointId, setSelectedTouchpointId] = useState<
    number | null
  >(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<
    number | null
  >(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [mediaToDelete, setMediaToDelete] = useState<string | null>(null);
  const [isDeletingMedia, setIsDeletingMedia] = useState(false);
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [pendingMediaFile, setPendingMediaFile] = useState<{file: File, type: 'photo' | 'audio'} | null>(null);
  const [mediaNote, setMediaNote] = useState('');
  const [isExportingFull, setIsExportingFull] = useState(false);

  // Local state for immediate UI updates
  const [localAnswers, setLocalAnswers] = useState<
    Record<number, { status: 'yes' | 'no' | 'na' | null; notes: string }>
  >({});
  
  // Store answered_at timestamp per touchpoint
  const [touchpointTimes, setTouchpointTimes] = useState<
    Record<number, string>
  >({});

  // Get media counts from Redux (for pending uploads)
  const mediaCountKey =
    id && selectedDepartmentId && selectedTouchpointId
      ? `${id}_${selectedDepartmentId}_${selectedTouchpointId}`
      : null;
  const mediaCounts = useAppSelector(state =>
    mediaCountKey
      ? state.assessments.touchpointMediaCounts[mediaCountKey]
      : null
  );

  // Fetch navigation data (sidebar) - First API call
  const {
    data: navigationData,
    isLoading: isLoadingNav,
    error: navError,
  } = useAssessmentNavigationQuery(id || '');
  

  // Fetch touchpoint standards (lazy loaded) - Second API call
  const {
    data: touchpointData,
    isLoading: isLoadingTouchpoint,
    refetch: refetchTouchpoint,
  } = useTouchpointStandardsQuery(
    id || '',
    selectedDepartmentId,
    selectedTouchpointId
  );

  // Submit assessment mutation
  const submitAssessmentMutation = useSubmitAssessmentMutation(id || '');

  // Note: Redux persist handles state persistence
  // Cleanup is not needed as it was causing state loss on navigation

  // Clear Redux state on component mount and unmount
  useEffect(() => {
    dispatch(clearAssessmentData());

    return () => {
      dispatch(clearAssessmentData());
    };
  }, [dispatch]);

  // Clear local answers when touchpoint changes
  useEffect(() => {
    if (selectedTouchpointId) {
      setLocalAnswers({});
    }
  }, [selectedTouchpointId]);

  // Refetch touchpoint data when navigation changes
  useEffect(() => {
    if (selectedDepartmentId && selectedTouchpointId && refetchTouchpoint) {
      refetchTouchpoint();
    }
  }, [selectedDepartmentId, selectedTouchpointId, refetchTouchpoint]);

  // Initialize localAnswers from Redux state when touchpoint data loads
  useEffect(() => {
    if (touchpointData?.titles && currentAssessment?.items) {
      const initialAnswers: Record<
        number,
        { status: 'yes' | 'no' | 'na' | null; notes: string }
      > = {};

      // Get answers from Redux store for this touchpoint's standards
      touchpointData.titles.forEach(
        (title: {
          title_id: number;
          standards: {
            standard_id: number;
            answer: string | null;
            comment: string | null;
          }[];
        }) => {
          title.standards.forEach(
            (standard: {
              standard_id: number;
              answer: string | null;
              comment: string | null;
            }) => {
              // Find answer in Redux by standardId
              const reduxAnswer = currentAssessment.items.find(
                item => item.question_id_snapshot === standard.standard_id
              );

              if (reduxAnswer) {
                initialAnswers[standard.standard_id] = {
                  status: reduxAnswer.answer as 'yes' | 'no' | 'na',
                  notes: reduxAnswer.comment || '',
                };
              }
            }
          );
        }
      );

      setLocalAnswers(initialAnswers);
    }
  }, [touchpointData, currentAssessment]);

  // Initialize touchpoint times from API data when touchpoint loads
  useEffect(() => {
    if (touchpointData && selectedTouchpointId) {
      // Get touchpoint_answered_at from API response (all standards in touchpoint share same time)
      if (touchpointData.touchpoint_answered_at && typeof touchpointData.touchpoint_answered_at === 'string') {
        setTouchpointTimes(prev => ({
          ...prev,
          [selectedTouchpointId]: touchpointData.touchpoint_answered_at as string
        }));
      }
    }
  }, [touchpointData, selectedTouchpointId]);

  // Auto-close sidebar on desktop
  useEffect(() => {
    if (!isMobile && isSidebarOpen) {
      setIsSidebarOpen(false);
    }
  }, [isMobile, isSidebarOpen]);

  // Initialize media counts when touchpoint data loads
  useEffect(() => {
    if (touchpointData && id && selectedDepartmentId && selectedTouchpointId) {
      // Only initialize if not already initialized
      if (!mediaCounts) {
        dispatch({
          type: 'assessments/initializeTouchpointMediaCounts',
          payload: {
            assessmentId: id,
            departmentId: selectedDepartmentId,
            touchpointId: selectedTouchpointId,
            currentImages: touchpointData.current_images_count,
            currentAudios: touchpointData.current_audios_count,
          },
        });
      }
    }
  }, [
    touchpointData,
    id,
    selectedDepartmentId,
    selectedTouchpointId,
    dispatch,
    mediaCounts,
  ]);

  // Auto-select first touchpoint and department when navigation data loads
  useEffect(() => {
    if (
      navigationData?.scope &&
      navigationData.scope.length > 0 &&
      !selectedTouchpointId
    ) {
      const firstItem = navigationData.scope[0];
      setSelectedTouchpointId(firstItem.touchpoint_id);
      setSelectedDepartmentId(firstItem.department_id);

      // Initialize Redux assessment state
      if (!currentAssessment || currentAssessment.id !== id) {
        dispatch(
          setCurrentAssessment({
            id: id || '',
            tenant_id: 1,
            property_id: navigationData.assessment.property_id,
            type: 'internal',
            status: 'draft',
            assessed_on: new Date().toISOString(),
            overall_score: 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            auditor_id: 1,
            template_id: '',
            department_id: '',
            touchpoint_id: '',
            media_attachments: [],
            items: [],
          })
        );
      }
    }
  }, [navigationData, selectedTouchpointId, id, currentAssessment, dispatch]);

  // Transform touchpoint data to component format
  const assessmentData: SharedAssessmentData = useMemo(() => {
    if (!navigationData || !touchpointData) {
      return {
        touchpoint: '',
        department: '',
        propertyType: 'Hotels' as PropertyType, // Default to Hotels instead of empty string
        titles: [],
        mediaFiles: [],
      };
    }

    const titles = touchpointData.titles.map(
      (title: AssessmentPerformanceTitle) => ({
        id: title.title_id,
        name:
          i18n.language === 'ar' ? title.title_name_ar : title.title_name_en,
        standards: title.standards.map(
          (standard: AssessmentPerformanceStandard) => ({
            id: standard.standard_id,
            question:
              i18n.language === 'ar'
                ? standard.question_ar
                : standard.question_en,
            subTouchpointTag: standard.sub_touchpoint
              ? i18n.language === 'ar'
                ? standard.sub_touchpoint.name_ar
                : standard.sub_touchpoint.name_en
              : '',
            status:
              localAnswers[standard.standard_id]?.status ?? standard.answer,
            notes:
              localAnswers[standard.standard_id]?.notes ??
              standard.comment ??
              '',
            answerId: standard.answer_id,
          })
        ),
      })
    );

    const scopeItem = navigationData.scope.find(
      s => s.touchpoint_id === selectedTouchpointId
    );

    return {
      touchpoint:
        i18n.language === 'ar'
          ? touchpointData.touchpoint_name_ar
          : touchpointData.touchpoint_name_en,
      department: scopeItem
        ? i18n.language === 'ar'
          ? scopeItem.department_name_ar
          : scopeItem.department_name_en
        : '',
      propertyType: (navigationData.assessment.property_type_name ||
        'Hotels') as PropertyType,
      titles,
      mediaFiles: [],
    };
  }, [
    navigationData,
    touchpointData,
    selectedTouchpointId,
    i18n.language,
    localAnswers,
  ]);

  // Handle standard updates - ONLY update Redux, NO API calls
  const handleStandardStatusChange = (
    titleId: number,
    standardId: number,
    status: 'yes' | 'no' | 'na'
  ) => {
    // Prevent managers from editing
    if (isReadOnly) {
      toast({
        title: t('common.error'),
        description: t(
          'assessments.perform.viewOnlyAccess',
          'You have view-only access and cannot edit this assessment'
        ),
        variant: 'destructive',
      });
      return;
    }
    
    // Check if time is selected for current touchpoint before allowing answer
    if (selectedTouchpointId && !touchpointTimes[selectedTouchpointId]) {
      toast({
        title: t('common.error'),
        description: t(
          'assessments.perform.selectTimeFirst',
          'Please select date and time before answering standards'
        ),
        variant: 'destructive',
      });
      return;
    }
    
    const title = assessmentData.titles.find(t => t.id === titleId);
    const standard = title?.standards.find(s => s.id === standardId);

    // Update local state for immediate UI feedback
    setLocalAnswers(prev => ({
      ...prev,
      [standardId]: {
        status,
        notes: prev[standardId]?.notes || standard?.notes || '',
      },
    }));

    // Only dispatch to Redux - NO API call
    dispatch(
      updateCurrentAssessmentAnswer({
        answerId: standard?.answerId
          ? String(standard.answerId)
          : `temp_${standardId}_${Date.now()}`,
        standardId: standardId,
        answer: status,
        comment: standard?.notes || undefined,
        answered_at: selectedTouchpointId ? touchpointTimes[selectedTouchpointId] : undefined,
        touchpointId: selectedTouchpointId || undefined,
        departmentId: selectedDepartmentId || undefined,
      })
    );
  };

  const handleStandardNotesChange = (
    titleId: number,
    standardId: number,
    notes: string
  ) => {
    // Prevent managers from editing
    if (isReadOnly) {
      return;
    }
    
    const title = assessmentData.titles.find(t => t.id === titleId);
    const standard = title?.standards.find(s => s.id === standardId);

    // Update local state for immediate UI feedback
    setLocalAnswers(prev => ({
      ...prev,
      [standardId]: {
        status: prev[standardId]?.status || standard?.status || null,
        notes,
      },
    }));

    // Only dispatch to Redux - NO API call
    const currentStatus =
      localAnswers[standardId]?.status || standard?.status || 'na';
    dispatch(
      updateCurrentAssessmentAnswer({
        answerId: standard?.answerId
          ? String(standard.answerId)
          : `temp_${standardId}_${Date.now()}`,
        standardId: standardId,
        answer: currentStatus,
        comment: notes || undefined,
        answered_at: selectedTouchpointId ? touchpointTimes[selectedTouchpointId] : undefined,
        touchpointId: selectedTouchpointId || undefined,
        departmentId: selectedDepartmentId || undefined,
      })
    );
  };

  // Handle touchpoint time change
  const handleTouchpointTimeChange = (answeredAt: string) => {
    if (!selectedTouchpointId) return;
    
    // Update local state
    setTouchpointTimes(prev => ({
      ...prev,
      [selectedTouchpointId]: answeredAt,
    }));

    // Update all answers for this touchpoint with the new timestamp
    if (currentAssessment?.items) {
      currentAssessment.items
        .filter(item => item.touchpoint_id_snapshot === selectedTouchpointId || item.touchpoint_id_snapshot === 0)
        .forEach(item => {
          dispatch(
            updateCurrentAssessmentAnswer({
              answerId: item.id,
              standardId: item.question_id_snapshot,
              answer: item.answer || '',
              comment: item.comment,
              answered_at: answeredAt,
              touchpointId: selectedTouchpointId || undefined,
              departmentId: selectedDepartmentId || undefined,
            })
          );
        });
    }
  };

  // Handle media icon click
  const handleMediaIconClick = () => {
    setIsMediaModalOpen(true);
  };

  // Handle media delete - show confirmation first
  const handleDeleteMedia = (mediaId: string) => {
    setMediaToDelete(mediaId);
  };

  // Confirm delete and call API
  const confirmDeleteMedia = async () => {
    if (!mediaToDelete || !id) return;

    setIsDeletingMedia(true);

    try {
      const client = new AxiosClient({
        endpoint: `/assessments/${id}/media/${mediaToDelete}`,
      });

      const response = await client.delete();

      if (response.status) {
        // Refetch touchpoint data to update media list
        refetchTouchpoint();
      } else {
        console.error('❌ Failed to delete media:', response.detail);
        toast({
          title: t('error'),
          description: response.detail || 'Failed to delete media',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('❌ Error deleting media:', error);
      toast({
        title: t('error'),
        description: 'Failed to delete media',
        variant: 'destructive',
      });
    } finally {
      setIsDeletingMedia(false);
      setMediaToDelete(null);
    }
  };

  // Cancel delete
  const cancelDeleteMedia = () => {
    setMediaToDelete(null);
  };

  // Export Full Assessment PDF - admin and super_admin only
  const exportFullAssessmentPDF = async () => {
    if (!id) {
      toast({
        title: t('common.error'),
        description: t(
          'assessments.perform.missingData',
          'Missing assessment data'
        ),
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsExportingFull(true);
      const endpoint = `/assessments/${id}/export-full-pdf`;

      // Create AxiosClient with token support
      const client = new AxiosClient({
        endpoint: endpoint,
        contentType: 'application/json',
        isToken: true,
        baseURL: API_BASE_URL,
      });

      // Make request using AxiosClient instance with the endpoint
      const response = await client.instance.get(endpoint, {
        responseType: 'blob',
        headers: {
          Accept: 'application/pdf',
        },
      });

      // Download file
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `assessment-full-${id}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);

      toast({
        title: t('common.success'),
        description: t(
          'assessments.perform.exportAllAssessmentSuccess',
          'Full assessment exported successfully'
        ),
      });
    } catch (error) {
      console.error('Full PDF export failed:', error);
      toast({
        title: t('common.error'),
        description: t(
          'assessments.perform.exportAllAssessmentFailed',
          'Failed to export full assessment'
        ),
        variant: 'destructive',
      });
    } finally {
      setIsExportingFull(false);
    }
  };

  // Handle Save as Draft - Clear Redux store after save
  const handleSaveAsDraft = async () => {
    try {
      // Group answered standards by touchpoint to validate answered_at per touchpoint
      const touchpointAnswers = new Map<number, { hasAnswers: boolean; hasTime: boolean }>();
      
      currentAssessment?.items
        .filter(item => !item.id.startsWith('temp_') && item.answer)
        .forEach(item => {
          const touchpointId = item.touchpoint_id_snapshot;
          if (!touchpointAnswers.has(touchpointId)) {
            touchpointAnswers.set(touchpointId, { hasAnswers: false, hasTime: false });
          }
          const touchpoint = touchpointAnswers.get(touchpointId)!;
          touchpoint.hasAnswers = true;
          if (item.answered_at) {
            touchpoint.hasTime = true;
          }
        });
      
      // Check if any touchpoint has answers but no time selected
      const missingTimes = Array.from(touchpointAnswers.entries())
        .filter(([_, data]) => data.hasAnswers && !data.hasTime);
      
      if (missingTimes.length > 0) {
        toast({
          title: t('common.error'),
          description: t(
            'assessments.perform.timeRequired',
            'Please select date and time for all answered touchpoints'
          ),
          variant: 'destructive',
        });
        return;
      }
      
      // Prepare assessment data from Redux store - only include items with valid answer IDs
      const assessmentData = currentAssessment
        ? {
            items: currentAssessment.items
              .filter(item => !item.id.startsWith('temp_')) // Only send items with real answer IDs
              .map(item => ({
                answer_id: item.id,
                standard_id: item.question_id_snapshot,
                answer: item.answer || '',
                comment: item.comment,
                answered_at: item.answered_at,
              })),
            media: currentAssessment.media_attachments.map(media => ({
              type: media.type,
              fileName: media.fileName,
              base64Data: media.base64Data,
              touchpointId: media.touchpointId,
            })),
          }
        : undefined;

      await submitAssessmentMutation.mutateAsync(assessmentData);

      dispatch(clearAssessmentData());

      toast({
        title: t('success'),
        description: t(
          'assessments.perform.savedAsDraft',
          'Assessment saved as draft'
        ),
      });

      navigate('/assessments');
    } catch (_error) {
      toast({
        title: t('error'),
        description: t(
          'assessments.perform.submitFailed',
          'Failed to save assessment'
        ),
        variant: 'destructive',
      });
    }
  };

  // Handle Submit Assessment - Clear Redux store after submit
  const handleSubmitAssessment = async () => {
    try {
      // Group answered standards by touchpoint to validate answered_at per touchpoint
      const touchpointAnswers = new Map<number, { hasAnswers: boolean; hasTime: boolean }>();
      
      currentAssessment?.items
        .filter(item => !item.id.startsWith('temp_') && item.answer)
        .forEach(item => {
          const touchpointId = item.touchpoint_id_snapshot;
          if (!touchpointAnswers.has(touchpointId)) {
            touchpointAnswers.set(touchpointId, { hasAnswers: false, hasTime: false });
          }
          const touchpoint = touchpointAnswers.get(touchpointId)!;
          touchpoint.hasAnswers = true;
          if (item.answered_at) {
            touchpoint.hasTime = true;
          }
        });
      
      // Check if any touchpoint has answers but no time selected
      const missingTimes = Array.from(touchpointAnswers.entries())
        .filter(([_, data]) => data.hasAnswers && !data.hasTime);
      
      if (missingTimes.length > 0) {
        toast({
          title: t('common.error'),
          description: t(
            'assessments.perform.timeRequired',
            'Please select date and time for all answered touchpoints'
          ),
          variant: 'destructive',
        });
        return;
      }

      // Prepare assessment data from Redux store - only include items with valid answer IDs
      const assessmentData = currentAssessment
        ? {
            items: currentAssessment.items
              .filter(item => !item.id.startsWith('temp_')) // Only send items with real answer IDs
              .map(item => ({
                answer_id: item.id,
                standard_id: item.question_id_snapshot,
                answer: item.answer || '',
                comment: item.comment,
                answered_at: item.answered_at,
              })),
            media: currentAssessment.media_attachments.map(media => ({
              type: media.type,
              fileName: media.fileName,
              base64Data: media.base64Data,
              touchpointId: media.touchpointId,
            })),
          }
        : undefined;

      await submitAssessmentMutation.mutateAsync(assessmentData);

      dispatch(clearAssessmentData());

      toast({
        title: t('success'),
        description: t(
          'assessments.perform.assessmentSubmitted',
          'Assessment submitted successfully'
        ),
      });

      navigate('/assessments');
    } catch (_error) {
      toast({
        title: t('error'),
        description: t(
          'assessments.perform.submitFailed',
          'Failed to submit assessment'
        ),
        variant: 'destructive',
      });
    }
  };

  // Handle media upload - store in Redux, send with submit
  const handleTouchpointMediaUpload = (type: 'photo' | 'audio') => {
    // Prevent managers from uploading media
    if (isReadOnly) {
      toast({
        title: t('common.error'),
        description: t(
          'assessments.perform.viewOnlyAccess',
          'You have view-only access and cannot edit this assessment'
        ),
        variant: 'destructive',
      });
      return;
    }
    
    // Validate limits BEFORE opening file dialog using Redux counts
    if (mediaCounts) {
      const totalImages = mediaCounts.currentImages + mediaCounts.pendingImages;
      const totalAudios = mediaCounts.currentAudios + mediaCounts.pendingAudios;

      if (type === 'photo' && totalImages >= 24) {
        // Show error - maximum 24 images per touchpoint
        toast({
          title: t('common.error'),
          description: t('assessments.perform.maxImagesReached'),
          variant: 'destructive',
        });
        return;
      }

      if (type === 'audio' && totalAudios >= 5) {
        // Show error - maximum 5 audios per touchpoint
        toast({
          title: t('common.error'),
          description: t('assessments.perform.maxAudiosReached'),
          variant: 'destructive',
        });
        return;
      }
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = type === 'photo' ? 'image/*' : 'audio/*';
    input.multiple = false; // Only one file at a time

    input.onchange = async (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      // Validate file size (25 MB limit for images)
      if (type === 'photo' && file.size > 25 * 1024 * 1024) {
        toast({
          title: t('common.error'),
          description: t('assessments.perform.imageTooLarge', 'Image must be less than 25 MB'),
          variant: 'destructive',
        });
        return;
      }

      // Store the file and open note dialog
      setPendingMediaFile({ file, type });
      setMediaNote('');
      setIsNoteDialogOpen(true);
    };

    input.click();
  };

  // Handle confirming media upload with note
  const handleConfirmMediaUpload = async () => {
    if (!pendingMediaFile) return;

    const { file, type } = pendingMediaFile;
    let processedFile = file;

    // Compress image if needed
    if (type === 'photo') {
      try {
        processedFile = await compressImage(file);
      } catch (_error) {
        console.error('Image compression failed:', _error);
        // Continue with original file if compression fails
      }
    }

    // Store file in Redux state for sending with submit
    // Convert file to base64 for storage
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;

      // Add to Redux media attachments
      if (currentAssessment) {
        // Create filename with note-date-assessmentid format
        // Sanitize note for filename (remove special characters)
        const sanitizedNote = mediaNote.trim().replace(/[^a-zA-Z0-9]/g, '_').substring(0, 50) || 'no_note';
        const timestamp = Date.now();
        const extension = type === 'photo' ? 'jpg' : 'mp3';
        const customFileName = `${sanitizedNote}-${timestamp}-${id}.${extension}`;
        
        const mediaItem: MediaAttachment = {
          id: `MEDIA_TEMP_${Date.now()}`,
          type: type,
          fileName: customFileName,
          fileSize: processedFile.size,
          mimeType: processedFile.type,
          base64Data: base64,
            touchpointId: selectedTouchpointId?.toString(),
            uploadedAt: new Date().toISOString(),
          };

          // Update Redux with media
          dispatch(
            setCurrentAssessment({
              ...currentAssessment,
              media_attachments: [
                ...currentAssessment.media_attachments,
                mediaItem,
              ],
            })
          );

          // Increment pending media count in Redux
          if (id && selectedDepartmentId && selectedTouchpointId) {
            dispatch(
              incrementPendingMedia({
                assessmentId: id,
                departmentId: selectedDepartmentId,
                touchpointId: selectedTouchpointId,
                mediaType: type,
              })
            );
          }
          
          // Close dialog and clear state
          setIsNoteDialogOpen(false);
          setPendingMediaFile(null);
          setMediaNote('');
        }
      };

      reader.readAsDataURL(processedFile);
  };

  // Check if all standards are answered
  const allStandards = assessmentData.titles.flatMap(title => title.standards);
  const answeredQuestions = allStandards.filter(
    standard =>
      standard.status === 'yes' ||
      standard.status === 'no' ||
      standard.status === 'na'
  );
  const isAllAnswered =
    allStandards.length > 0 && answeredQuestions.length === allStandards.length;

  // Calculate completion percentage from navigation data
  const completionPercentage =
    navigationData?.statistics?.completion_percentage || 0;

  // Check if user can export all assessment (admin and super_admin only)
  const canExportAll = hasRole('super_admin') || hasRole('admin');

  // Handle touchpoint click
  const handleTouchpointClick = (touchpointId: number) => {
    // Find the department for this touchpoint from navigation data
    const scopeItem = navigationData?.scope.find(
      s => s.touchpoint_id === touchpointId
    );
    if (scopeItem) {
      setSelectedDepartmentId(scopeItem.department_id);
    }
    setSelectedTouchpointId(touchpointId);
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  // Handle department click
  const handleDepartmentClick = (departmentId: string) => {
    setSelectedDepartmentId(parseInt(departmentId));
  };

  // Get current touchpoint name from navigation data
  const currentTouchpoint = useMemo(() => {
    if (!navigationData || !selectedTouchpointId) return null;

    const scopeItem = navigationData.scope.find(
      item => item.touchpoint_id === selectedTouchpointId
    );

    return scopeItem
      ? {
          name:
            i18n.language === 'ar'
              ? scopeItem.touchpoint_name_ar
              : scopeItem.touchpoint_name_en,
        }
      : null;
  }, [navigationData, selectedTouchpointId, i18n.language]);

  // Create property data for sidebar from navigation data
  const propertyData = useMemo(() => {
    if (!navigationData) {
      return {
        id: '',
        name: '',
        icon: '',
        color: '',
        departments: [],
        actualProperty: undefined,
      };
    }

    // Group scope items by department
    const departmentMap = new Map();
    const touchpointDuplicates = new Set();

    navigationData.scope.forEach(scopeItem => {
      if (!departmentMap.has(scopeItem.department_id)) {
        departmentMap.set(scopeItem.department_id, {
          id: scopeItem.department_id.toString(),
          name:
            i18n.language === 'ar'
              ? scopeItem.department_name_ar
              : scopeItem.department_name_en,
          icon: '',
          touchpoints: [],
        });
      }

      const dept = departmentMap.get(scopeItem.department_id);

      // Check if touchpoint already exists in this department to avoid duplicates
      const touchpointExists = dept.touchpoints.some(
        (tp: { id: string }) => tp.id === scopeItem.touchpoint_id.toString()
      );

      if (!touchpointExists) {
        dept.touchpoints.push({
          id: scopeItem.touchpoint_id.toString(),
          name:
            i18n.language === 'ar'
              ? scopeItem.touchpoint_name_ar
              : scopeItem.touchpoint_name_en,
          icon: '',
          titles: [],
        });
      } else {
        const duplicateKey = `${scopeItem.department_id}_${scopeItem.touchpoint_id}`;
        if (!touchpointDuplicates.has(duplicateKey)) {
          touchpointDuplicates.add(duplicateKey);
        }
      }
    });

    return {
      id: navigationData.assessment.property_type_name,
      name: navigationData.assessment.property_type_name,
      icon: '',
      color: '',
      departments: Array.from(departmentMap.values()),
      actualProperty: {
        id: navigationData.assessment.property_id,
        name: navigationData.assessment.property_name,
        property_type_id: 1, // Default value as it's required
        is_active: true, // Default value as it's required
      },
    };
  }, [navigationData, i18n.language]);

  // Calculate touchpoint progress for ALL touchpoints from navigation data
  const touchpointProgress = useMemo(() => {
    if (!navigationData) return [];

    // Map status from navigation data to progress format
    return navigationData.scope.map(scopeItem => {
      // Status to progress mapping
      let totalStandards = 1; // Default to 1 to avoid division by zero
      let answeredStandards = 0;

      if (scopeItem.status === 'completed') {
        answeredStandards = 1;
        totalStandards = 1;
      } else if (scopeItem.status === 'in_progress') {
        // Partially answered - use a value that's neither 0 nor 100%
        answeredStandards = 1;
        totalStandards = 2;
      } else if (scopeItem.status === 'unanswered') {
        answeredStandards = 0;
        totalStandards = 1;
      }

      return {
        touchpointId: scopeItem.touchpoint_id.toString(),
        totalStandards,
        answeredStandards,
      };
    });
  }, [navigationData]);

  // Loading state
  if (isLoadingNav) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (navError || !navigationData) {
    const errorMessage = navError?.message || 'Assessment not found';
    const errorDetail =
      (navError as any)?.response?.data?.detail ||
      (navError as any)?.response?.data?.error ||
      'The assessment you are trying to access does not exist or has been deleted.';

    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md p-8 bg-white rounded-lg shadow-lg">
          <div className="mb-6">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-10 h-10 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t('common.error')}
            </h2>
            <p className="text-gray-600 mb-2 font-medium">{errorMessage}</p>
            <p className="text-sm text-gray-500 mb-6">{errorDetail}</p>
          </div>
          <Button onClick={() => navigate('/assessments')} className="w-full">
            {t('common.back')} to Assessments
          </Button>
        </div>
      </div>
    );
  }

  return (
    <ContentProtectionWrapper
      preventScreenshot={true}
      preventCopy={true}
      preventRightClick={true}
      preventTextSelection={true}
      preventPrint={true}
      preventDevTools={false}
      watermark={false}
      showSecurityBanner={true}
    >
      <div
        className={cn(
          'flex flex-col min-h-screen bg-gray-50',
          isRTL ? 'rtl' : 'ltr'
        )}
      >
        {/* Header with Back Button - Responsive */}
        <div className="bg-primary text-primary-foreground p-3 sm:p-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between gap-2 sm:gap-4">
              {/* Left/Right side - Switch based on language */}
              {isRTL ? (
                // RTL: Spacer or Export button on LEFT
                canExportAll ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={exportFullAssessmentPDF}
                    disabled={isExportingFull}
                    className="text-primary-foreground hover:bg-primary-foreground/10 flex-shrink-0 flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                  >
                    <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden md:inline">
                      {isExportingFull
                        ? t('common.exporting', 'Exporting...')
                        : t(
                            'assessments.perform.exportAllAssessment',
                            'Export All Assessment'
                          )}
                    </span>
                    <span className="md:hidden">
                      {isExportingFull
                        ? t('common.loading', 'Loading...')
                        : t('common.export', 'Export')}
                    </span>
                  </Button>
                ) : (
                  <div className="w-[60px] sm:w-[100px] flex-shrink-0"></div>
                )
              ) : (
                // LTR: Back button on LEFT
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/assessments')}
                  className="text-primary-foreground hover:bg-primary-foreground/10 flex-shrink-0 flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                >
                  <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden xs:inline">
                    {t('common.back', 'Back')}
                  </span>
                </Button>
              )}

              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsSidebarOpen(true)}
                className="lg:hidden text-primary-foreground hover:bg-primary-foreground/10 flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
              >
                <Menu className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden xs:inline">
                  {t('assessments.perform.menu', 'Menu')}
                </span>
              </Button>

              {/* Center - Title */}
              <div className="flex-1 min-w-0 text-center">
                <h1 className="text-sm sm:text-xl font-semibold truncate">
                  {navigationData.assessment.subject}
                </h1>
                <p className="text-xs sm:text-sm text-primary-foreground/80 truncate">
                  {navigationData.assessment.property_name}
                </p>
                {navigationData.assessment.notes && (
                  <p className="text-xs sm:text-sm text-primary-foreground/70 italic mt-1 line-clamp-2">
                    {t('assessments.perform.notes', 'Notes')}:{' '}
                    {navigationData.assessment.notes}
                  </p>
                )}
              </div>

              {/* Right/Left side - Switch based on language */}
              {isRTL ? (
                // RTL: Back button on RIGHT
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/assessments')}
                  className="text-primary-foreground hover:bg-primary-foreground/10 flex-shrink-0 flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                >
                  <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 rotate-180" />
                  <span className="hidden xs:inline">
                    {t('common.back', 'Back')}
                  </span>
                </Button>
              ) : (
                // LTR: Export button or spacer on RIGHT
                canExportAll ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={exportFullAssessmentPDF}
                    disabled={isExportingFull}
                    className="text-primary-foreground hover:bg-primary-foreground/10 flex-shrink-0 flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                  >
                    <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden md:inline">
                      {isExportingFull
                        ? t('common.exporting', 'Exporting...')
                        : t(
                            'assessments.perform.exportAllAssessment',
                            'Export All Assessment'
                          )}
                    </span>
                    <span className="md:hidden">
                      {isExportingFull
                        ? t('common.loading', 'Loading...')
                        : t('common.export', 'Export')}
                    </span>
                  </Button>
                ) : (
                  <div className="w-[60px] sm:w-[100px] flex-shrink-0"></div>
                )
              )}
            </div>
          </div>
        </div>

        {/* View-Only Mode Banner for Managers */}
        {isReadOnly && (
          <div className="bg-blue-50 border-b border-blue-200 p-3 sm:p-4">
            <div className="max-w-7xl mx-auto">
              <p className="text-sm text-blue-800 font-medium">
                👁️{' '}
                {t(
                  'assessments.perform.viewOnlyMode',
                  'You are viewing this assessment in read-only mode. You cannot make any changes.'
                )}
              </p>
            </div>
          </div>
        )}

        {/* Due Date Warning */}
        {(navigationData.assessment as { is_due_date_passed?: boolean })
          .is_due_date_passed && (
          <div className="bg-red-50 border-b border-red-200 p-3 sm:p-4">
            <div className="max-w-7xl mx-auto">
              <p className="text-sm text-red-800 font-medium">
                ⚠️{' '}
                {t(
                  'assessments.perform.dueDatePassed',
                  'The due date for this assessment has passed'
                )}{' '}
                ({navigationData.assessment.due_date})
              </p>
            </div>
          </div>
        )}

        {/* Main Content with Sidebar - Responsive Layout */}
        <AssessmentMainContent
          selectedDepartmentId={selectedDepartmentId?.toString() || null}
          selectedTouchpointId={selectedTouchpointId?.toString() || null}
          isSidebarOpen={isSidebarOpen}
          onDepartmentClick={handleDepartmentClick}
          onTouchpointClick={id => handleTouchpointClick(parseInt(id))}
          onSidebarToggle={() => setIsSidebarOpen(false)}
          assessmentData={assessmentData}
          touchpointProgress={touchpointProgress}
          overallCompletionPercentage={completionPercentage}
          currentTouchpoint={currentTouchpoint}
          onStandardStatusChange={handleStandardStatusChange}
          onStandardNotesChange={handleStandardNotesChange}
          onTouchpointTimeChange={handleTouchpointTimeChange}
          onMediaUpload={handleTouchpointMediaUpload}
          touchpointAnsweredAt={selectedTouchpointId ? touchpointTimes[selectedTouchpointId] : undefined}
          propertyData={propertyData}
          isLoadingTouchpoint={isLoadingTouchpoint}
          currentImagesCount={
            (touchpointData?.current_images_count ?? 0) +
            (mediaCounts?.pendingImages ?? 0)
          }
          currentAudiosCount={
            (touchpointData?.current_audios_count ?? 0) +
            (mediaCounts?.pendingAudios ?? 0)
          }
          maxImages={touchpointData?.max_images ?? 24}
          maxAudios={touchpointData?.max_audios ?? 5}
          onMediaIconClick={handleMediaIconClick}
          isReadOnly={isReadOnly}
        />

        {/* Footer Section - Assessment Progress and Submit Button */}
        {!isReadOnly && (
          <AssessmentFooter
            currentTouchpoint={currentTouchpoint}
            assessmentData={assessmentData}
            isAllAnswered={isAllAnswered}
            onSubmitAssessment={handleSubmitAssessment}
            onSaveAsDraft={handleSaveAsDraft}
            isPending={submitAssessmentMutation.isPending}
          />
        )}

        {/* Media Viewer Modal */}
        {selectedTouchpointId && (
          <MediaViewerModal
            isOpen={isMediaModalOpen}
            onClose={() => setIsMediaModalOpen(false)}
            media={touchpointData?.media || []}
            onDelete={handleDeleteMedia}
            isDeleting={isDeletingMedia}
            touchpointName={currentTouchpoint?.name || ''}
            isBlocked={!!mediaToDelete} // Block interaction when confirmation dialog is open
            isReadOnly={isReadOnly} // Hide delete button for managers
          />
        )}

        {/* Media Note Dialog */}
        <Dialog open={isNoteDialogOpen} onOpenChange={setIsNoteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t('assessments.perform.addMediaNote', 'Add Note to Media')}
              </DialogTitle>
              <DialogDescription>
                {t(
                  'assessments.perform.addMediaNoteDescription',
                  'Add an optional note or description for this media file. The note will be part of the filename.'
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="mediaNote">
                  {t('assessments.perform.noteLabel', 'Note (optional)')}
                </Label>
                <Input
                  id="mediaNote"
                  value={mediaNote}
                  onChange={(e) => setMediaNote(e.target.value)}
                  placeholder={t('assessments.perform.notePlaceholder', 'Enter a note or description...')}
                  maxLength={50}
                />
                <p className="text-xs text-muted-foreground">
                  {t('assessments.perform.noteHint', 'Max 50 characters. Special characters will be replaced with underscores.')}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsNoteDialogOpen(false);
                  setPendingMediaFile(null);
                  setMediaNote('');
                }}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button onClick={handleConfirmMediaUpload}>
                {t('assessments.perform.uploadMedia', 'Upload')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          open={!!mediaToDelete}
          onOpenChange={open => !open && cancelDeleteMedia()}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t('common.confirm', 'Confirm')}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t(
                  'assessments.perform.deleteMediaConfirm',
                  'Are you sure you want to delete this media file? This action cannot be undone.'
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={cancelDeleteMedia}
                disabled={isDeletingMedia}
              >
                {t('common.cancel', 'Cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDeleteMedia}
                disabled={isDeletingMedia}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeletingMedia
                  ? t('common.deleting', 'Deleting...')
                  : t('common.delete', 'Delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ContentProtectionWrapper>
  );
}
